import React, { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import './AssetsGallery.scss'
import ComboBox from '@react/react-spectrum/ComboBox'
import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Wait from '@react/react-spectrum/Wait'
import Rule from '@react/react-spectrum/Rule'
import Checkbox from '@react/react-spectrum/Checkbox'
import { useDsnApiV2 } from 'services/axios'
import { getLivedemosWorkspaceDetailsUrl, getLivedemosWorkspacesUrl } from 'services/environment'
import { useAssets } from './hooks/useAssets'
import { useProject } from 'hooks'
import { ImageBox } from './partials/ImageBox'
import { useAssetUpload } from './hooks/useAssetUpload'
import { useProjectSearch } from './hooks/useProjectSearch'

const SESSION_STORAGE_KEY = 'dsn-selected-livedemos-workspace'

function getSessionSelection(projectId) {
  try {
    const stored = sessionStorage.getItem(`${SESSION_STORAGE_KEY}-${projectId}`)
    return stored || null
  } catch {
    return null
  }
}

function setSessionSelection(projectId, workspaceId) {
  try {
    if (workspaceId) {
      sessionStorage.setItem(`${SESSION_STORAGE_KEY}-${projectId}`, workspaceId)
    } else {
      sessionStorage.removeItem(`${SESSION_STORAGE_KEY}-${projectId}`)
    }
  } catch {
    // ignore
  }
}

export default function AssetsGallery({ componentId, imageFieldName, onClose, onSubmit }) {
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [selectedAssetTypes, setSelectedAssetTypes] = useState(['other', 'product'])
  const { project } = useProject()
  const projectMeta = project.meta ?? {}
  const defaultLivedemosWorkspaceId = project.workspace?.livedemosWorkspaceId ?? null
  const candidateDemoPilotProjectId =
    getSessionSelection(projectMeta?.id) ?? defaultLivedemosWorkspaceId ?? projectMeta?.demoPilotProjectId ?? null
  const { projectId, screenName } = useParams()
  const [{ data: demoPilotProjects, loading: demoPilotProjectsLoading, error: demoPilotProjectsError }] = useDsnApiV2(
    '/demopilot/projects',
    {}
  )

  // Only treat the candidate as selected when it exists in the fetched list, so a
  // stale/removed livedemosWorkspaceId doesn't trigger a 404 asset fetch or a dead link.
  const selectedDemoPilotProjectId = useMemo(() => {
    if (!candidateDemoPilotProjectId) return null
    const list = Array.isArray(demoPilotProjects) ? demoPilotProjects : []
    return list.some((p) => p.id === candidateDemoPilotProjectId) ? candidateDemoPilotProjectId : null
  }, [candidateDemoPilotProjectId, demoPilotProjects])

  const {
    assets,
    productAssets,
    otherAssets,
    loading: assetsLoading,
    error: assetsError,
  } = useAssets(selectedDemoPilotProjectId)

  const { uploadAsset, isLoading: isUploading } = useAssetUpload({
    projectId,
    demoPilotProjectId: selectedDemoPilotProjectId,
    screenName,
    componentMetaId: componentId,
    fieldName: imageFieldName,
  })

  const {
    searchInput,
    filteredOptions,
    showMenu,
    handleInputChange,
    handleSelect,
    handleMenuToggle,
    handleFocus,
    handleBlur,
  } = useProjectSearch(demoPilotProjects, selectedDemoPilotProjectId)

  // default workspace is at the top
  const sortedOptions = useMemo(() => {
    if (!defaultLivedemosWorkspaceId || !filteredOptions) return filteredOptions
    return [...filteredOptions].sort((a, b) => {
      if (a.value === defaultLivedemosWorkspaceId) return -1
      if (b.value === defaultLivedemosWorkspaceId) return 1
      return 0
    })
  }, [filteredOptions, defaultLivedemosWorkspaceId])

  // add a default tag to the default workspace
  const renderItem = (option) => (
    <div>
      <div>{option.label}</div>
      {option.value === defaultLivedemosWorkspaceId && (
        <div style={{ fontSize: '11px', color: '#6e6e6e' }}>Default</div>
      )}
    </div>
  )

  const handleProjectSelection = (projectKey) => {
    if (!projectKey) return
    setSessionSelection(projectMeta?.id, projectKey)
  }

  const onProjectSelect = (option) => {
    const projectId = handleSelect(option)
    handleProjectSelection(projectId)
  }

  const handleUpload = async () => {
    if (!selectedAsset) return
    const url = await uploadAsset(selectedAsset)
    if (url) {
      onSubmit(url)
    }
  }

  const toggleAssetType = (value, isSelected) => {
    setSelectedAssetTypes((prev) => (!isSelected ? prev.filter((v) => v !== value) : [...prev, value]))
  }

  const filteredAssets = useMemo(() => {
    const hasProduct = selectedAssetTypes.includes('product')
    const hasOther = selectedAssetTypes.includes('other')

    if (hasProduct && hasOther) return assets
    if (hasOther) return otherAssets
    if (hasProduct) return productAssets
    return []
  }, [selectedAssetTypes, assets, otherAssets, productAssets])

  const footer = () => (
    <div className='AssetsGallery__footer'>
      <Button variant='primary' onClick={onClose} disabled={isUploading}>
        Close
      </Button>
      <Button variant='primary' disabled={!selectedAsset || isUploading} onClick={handleUpload}>
        {isUploading ? <Wait size='S' /> : 'Upload'}
      </Button>
    </div>
  )

  const assetsGrid = (assets) => (
    <div className='AssetsGallery__gridContainer'>
      <div className='AssetsGallery__grid'>
        {assets.map((asset) => (
          <ImageBox
            key={asset.id}
            asset={asset}
            projectId={selectedDemoPilotProjectId}
            isSelected={selectedAsset?.id === asset.id}
            onSelect={(asset) => setSelectedAsset(asset)}
          />
        ))}
      </div>
    </div>
  )

  if (demoPilotProjectsLoading) {
    return (
      <div className='AssetsGallery'>
        <div className='AssetsGallery__loading'>
          <Wait size='L' />
          Loading workspaces...
        </div>
        {footer()}
      </div>
    )
  }

  if (demoPilotProjectsError || !demoPilotProjects) {
    console.error('Failed to fetch Livedemos workspaces', demoPilotProjectsError)
    return (
      <div className='AssetsGallery'>
        <div className='AssetsGallery__centeredInfo'>Failed to fetch Livedemos workspaces</div>
        {footer()}
      </div>
    )
  }

  return (
    <div className='AssetsGallery'>
      <div className='AssetsGallery__content'>
        <div className='AssetsGallery__formField'>
          <div className='AssetsGallery__comboBoxContainer'>
            <FieldLabel label='Select Workspace'>
              <ComboBox
                placeholder='Search Workspaces...'
                id='component-sidebar-assets-gallery'
                value={searchInput}
                options={sortedOptions}
                showMenu={showMenu}
                onChange={handleInputChange}
                onSelect={onProjectSelect}
                onMenuToggle={handleMenuToggle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                renderItem={renderItem}
              />
            </FieldLabel>
            <div className='AssetsGallery__zdpLinkContainer'>
              {!demoPilotProjectsError && demoPilotProjects.length === 0 && (
                <div className='AssetsGallery__centeredInfo'>No workspaces...</div>
              )}
              <a
                href={getLivedemosWorkspaceDetailsUrl(selectedDemoPilotProjectId) || getLivedemosWorkspacesUrl()}
                target='_blank'
                rel='noopener noreferrer'
              >
                Import more assets...
              </a>
            </div>
            <Rule variant='small' />
          </div>
          {assetsLoading && (
            <div className='AssetsGallery__loading'>
              <Wait />
              Loading assets...
            </div>
          )}
          {assetsError && <div className='AssetsGallery__centeredInfo'>Failed to load assets.</div>}
          {!assetsLoading &&
            !assetsError &&
            assets.length === 0 &&
            selectedDemoPilotProjectId &&
            demoPilotProjects.length > 0 && (
              <div className='AssetsGallery__centeredInfo'>
                <div className='AssetsGallery__centeredInfo__title'>Workspace does not have any assets.</div>
              </div>
            )}
          {!assetsLoading && !assetsError && assets.length > 0 && selectedDemoPilotProjectId && (
            <>
              <div className='AssetsGallery__selectAssetTypesContainer'>
                <FieldLabel label='Select Asset Types'>
                  <div className='AssetsGallery__selectAssetTypesContainer__flex'>
                    <Checkbox
                      value='other'
                      checked={selectedAssetTypes.includes('other')}
                      onChange={(isSelected) => toggleAssetType('other', isSelected)}
                      quiet
                    >
                      Other
                    </Checkbox>

                    <Checkbox
                      value='product'
                      checked={selectedAssetTypes.includes('product')}
                      onChange={(isSelected) => toggleAssetType('product', isSelected)}
                      quiet
                    >
                      Product
                    </Checkbox>
                  </div>
                </FieldLabel>
              </div>
              {assetsGrid(filteredAssets)}
            </>
          )}
        </div>
      </div>
      {footer()}
    </div>
  )
}
